import {ConstantProperty, ConstantPropertyDataType} from "../constant-property";
import {KeyMap, ModelID} from "../../_typing/generic-types";
import {AGGREGATION_TYPES, AggregationType} from "../../shared/globals";
import {EventConfigColumn} from "../../_typing/config/config-column";

type AttributeDataType = 'string' | 'datetime';

export type ConstantValue = string | number | null;
export type AttributeValue = string | null;

export abstract class BaseGenericField {
    abstract type: string;
}

export abstract class BaseEditableField extends BaseGenericField {
    editable: boolean;
    locked: boolean;
    data_type: ConstantPropertyDataType;
    created_on?: string;
    changed_on?: string;
}

export class InvalidField extends BaseGenericField {
    type: 'invalid';
    data_type: 'invalid';
    locked?: boolean;
}

export class AttributeField extends BaseEditableField {
    type: 'attribute';
    data_type: AttributeDataType;
    value: AttributeValue;
}

export class ConstantField extends BaseEditableField {
    type: 'constant' = 'constant';
    data_type: ConstantPropertyDataType;
    value: ConstantValue;

    constructor(dataType?: ConstantPropertyDataType) {
        super();
        this.data_type = dataType ?? 'string';
    }
}

export class AggregatedConstantField extends ConstantField {
    aggregation: AggregationType;

    constructor(dataType?: ConstantPropertyDataType, aggregation?: AggregationType) {
        super(dataType);
        this.aggregation = aggregation ?? 'default';
    }
}

export class FileField extends BaseEditableField {
    type: 'file' = 'file';
    filename?: string;
    mimetype?: string;
    error?: string;
    is_set: boolean;

    constructor() {
        super();
    }
}

export type GenericField = AttributeField | ConstantField | AggregatedConstantField | InvalidField | FileField;

export function isConstant(constant: GenericField): constant is ConstantField {
    return (constant as ConstantField)?.type === 'constant';
}

export function isConstantValue(constant: unknown): constant is ConstantValue {
    return (
        typeof constant === 'string' ||
        typeof constant === 'number' ||
        constant === null
    );
}

export function isAggregatedConstant(constant: GenericField): constant is AggregatedConstantField {
    return (constant as AggregatedConstantField)?.type === 'constant' &&
        'aggregation' in constant;
}

export function isFile(constant: GenericField): constant is FileField {
    return (constant as FileField)?.type === 'file';
}

export function isInvalid(constant: GenericField): constant is InvalidField {
    return (constant as InvalidField)?.type === 'invalid';
}

export type GenericConstantData = KeyMap<KeyMap<GenericField>>;

export interface GenericConstantApiResponse {
    data?: GenericConstantData;
}


export class ComponentConstantField {
    constant_property_id: ModelID;
    component_id: ModelID;
    value: ConstantValue;

    constructor(component_id: ModelID, constant_property_id: ModelID, value: ConstantValue) {
        this.component_id = component_id;
        this.constant_property_id = constant_property_id;
        if (typeof value === 'string') {
            this.value = value.trim();
        } else {
            this.value = value;
        }
    }
}

export class ConstantRelationshipAggregationRequest {
    property_id: ModelID;
    method: string;
    type_id: ModelID;
    type: "component" | "event";
    aggregation_target?: "property" | "entity";

    constructor(column: EventConfigColumn, type: 'component' | 'event', typeId?: ModelID) {
        const isComponentType = column.type === 'component_type';
        return {
            property_id: column.id,
            method: AGGREGATION_TYPES[column.format?.aggregation_type] || 'default',
            type_id: column.related_type_id || typeId,
            type: type,
            aggregation_target: isComponentType ? 'entity' : 'property'
        }
    }
}

export interface GenericConstantRequest {
    component_ids?: ModelID[];
    constant_property_ids?: ModelID[];
    attributes?: string[];
    aggregation: ConstantRelationshipAggregationRequest[];
    relationships: GenericConstantRelationshipRequest[];
}

export interface GenericConstantRelationshipRequest {
    type_id: ModelID;
    type: string;
    constant_property_ids?: ModelID[];
    attributes?: string[];
    aggregation?: ConstantRelationshipAggregationRequest[];
}
